<script lang="ts">
import type { CodeLanguage, LineWrappingMode, TextareaProps } from '#layers/ui/public';
import { tv } from 'tailwind-variants';

const codeBlock = tv({
	slots: {
		base: '',
	},
	variants: {
		expandable: {
			true: {
				base: 'max-h-full min-h-0',
			},
			false: {
				base: 'max-h-80 min-h-30 resize-y',
			},
		},
	},
});

const __DsBlock = {
	type: 'input',
	icon: 'lucide:braces',
	name: '$t:blocks.input_code.name',
	description: '$t:blocks.input_code.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ['json'] },
	options: [
		{ name: 'modelValue', label: '$t:blocks.common.props.value', model: true },
		{ name: 'placeholder', label: '$t:blocks.common.props.placeholder' },
		{ name: 'codeLanguage', label: '$t:blocks.input_code.props.code_language' },
		{ name: 'variant', label: '$t:blocks.common.props.variant' },
		{ name: 'size', label: '$t:blocks.common.props.size' },
		{ name: 'block', label: '$t:blocks.common.props.block' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
		{ name: 'copy', label: '$t:blocks.common.props.copy' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'inert', label: '$t:blocks.common.props.inert' },
		{ name: 'lineNumbers', label: '$t:blocks.input_code.props.line_numbers' },
		{ name: 'lint', label: '$t:blocks.input_code.props.lint' },
		{ name: 'lineWrapping' },
	],
}</script>

<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { JsonValue } from '#layers/data-model/public';
import { computed, ref, watch } from 'vue';
import { defineBlock } from '#layers/block-engine/public';
import { isPrimitiveTypeJson, parseRawValue, serializeRawValue } from '#layers/data-model/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { InputCode, useDisabledNotice } from '#layers/ui/public';

type JsonParseResult =
	| { ok: true; value: JsonValue }
	| { ok: false };

interface CodeBlockProps extends InputBlockProps, Pick<TextareaProps, 'placeholder' | 'size'> {
	variant?: 'outline' | 'subtle';
	codeLanguage?: CodeLanguage;
	block?: boolean;
	copy?: boolean;
	inert?: boolean;
	lineNumbers?: boolean;
	lint?: boolean;
	lineWrapping?: LineWrappingMode;
	ui?: Partial<typeof codeBlock.slots>;
	maxHeightBeforeExpand?: number;
}

const props = withDefaults(defineProps<CodeBlockProps>(), {
	block: true,
	size: 'md',
	variant: 'outline',
	codeLanguage: 'json',
	lineNumbers: true,
});

const theme = computed(() => codeBlock());

;

const jsonPrimitiveType = { name: 'json' } as const;

/**
 * Serializes native JSON values into the string document consumed by CodeMirror.
 */
function serializeJsonEditorValue(value: JsonValue | undefined): string {
	return serializeRawValue(value, jsonPrimitiveType);
}

/**
 * Parses editor text into a native JSON value without throwing while the user types.
 */
function parseJsonEditorText(text: string): JsonParseResult {
	try {
		return { ok: true, value: parseRawValue(text, jsonPrimitiveType) as JsonValue };
	}
	catch {
		return { ok: false };
	}
}

// JsonValue includes boolean, so Vue would cast an absent model prop to false
// unless the default explicitly stays undefined for primitive-field fallback.
const model = defineModel<JsonValue | string | undefined>({ default: undefined });

const { modelValue, disabled, disabledNotice, field } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const isNativeJsonField = computed(() => !!field.value && isPrimitiveTypeJson(field.value.type));
const editorText = ref('');

// Tracks the model write triggered by the editor so the watcher can ignore
// exactly one matching echo without suppressing later external changes.
let pendingEditorModelValue: JsonValue | undefined;
let hasPendingEditorModelValue = false;

watch(
	() => [isNativeJsonField.value, modelValue.value] as const,
	([nativeJsonField, value]) => {
		if (!nativeJsonField) return;

		if (hasPendingEditorModelValue) {
			const isEditorEcho = Object.is(value, pendingEditorModelValue);

			hasPendingEditorModelValue = false;
			pendingEditorModelValue = undefined;

			if (isEditorEcho) return;
		}

		editorText.value = serializeJsonEditorValue(value);
	},
	{ immediate: true },
);

const inputModel = computed<string>({
	get: () => {
		if (isNativeJsonField.value) return editorText.value;
		return typeof modelValue.value === 'string' ? modelValue.value : '';
	},
	set: (text) => {
		// Non-field usages already provide serialized text, so keep them as pass-through strings.
		if (!isNativeJsonField.value) {
			modelValue.value = text;
			return;
		}

		editorText.value = text;

		const parsed = parseJsonEditorText(text);

		if (parsed.ok) {
			pendingEditorModelValue = parsed.value;
			hasPendingEditorModelValue = true;
			modelValue.value = parsed.value;
		}
	},
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<InputCode
			v-model="inputModel"
			:class="theme.base({ class: props.ui?.base, expandable: maxHeightBeforeExpand !== undefined })"
			:code-language
			:line-numbers
			:lint
			:line-wrapping
			:placeholder
			:variant
			:size
			:block
			:copy
			:disabled
			:readonly
			:inert
			:max-height-before-expand
		/>
	</DTooltip>
</template>
